/**
 * Unified Input Area Component
 *
 * Single textarea that handles all input types (text, URL, audio).
 * Content-based type detection mirrors matchScenario() in useDemoPipeline.
 * Supports two mic modes: Live Transcription and Record Audio.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Send, Link2, Mic, Type } from 'lucide-react';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useInputTypeDetection } from '../../hooks/useInputTypeDetection';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { usePiiScanner } from '../../hooks/usePiiScanner';
import { AudioAttachmentChip } from './AudioAttachmentChip';
import { PiiWarningBanner } from './PiiWarningBanner';
import { MicButton, type MicMode } from './MicButton';

interface InputAreaProps {
  onSubmit: (input: string, inputType: 'TEXT' | 'LINK' | 'AUDIO') => void;
  onSubmitAudio?: (file: Blob, metadata: { duration: number }) => void;
  isProcessing: boolean;
}

const ICON_MAP = {
  'type': Type,
  'link-2': Link2,
  'mic': Mic,
} as const;

export function InputArea({ onSubmit, onSubmitAudio, isProcessing }: InputAreaProps) {
  const [value, setValue] = useState('');
  const [wasTranscribed, setWasTranscribed] = useState(false);
  const [dismissedPii, setDismissedPii] = useState(false);
  const [micMode, setMicMode] = useState<MicMode>(
    () => (localStorage.getItem('crucio-mic-mode') as MicMode) || 'transcribe'
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Live transcription (Web Speech API)
  const speech = useSpeechRecognition();

  // Audio recording (MediaRecorder API)
  const recorder = useAudioRecorder();

  // Client-side PII detection (Rust WASM)
  // Destructure to get stable refs — the hook returns a new object each render,
  // but scan/clear are useCallback-stable and safe for dep arrays.
  const { scan: piiScan, result: piiResult, hasPii, clear: piiClear } = usePiiScanner();

  const detected = useInputTypeDetection(value, wasTranscribed);
  const BadgeIcon = ICON_MAP[detected.icon];

  // Determine which mic system is active
  const isTranscribeMode = micMode === 'transcribe';
  const isMicActive = isTranscribeMode ? speech.isListening : recorder.isRecording;
  const micError = isTranscribeMode ? speech.error : recorder.error;

  // Can submit when there's text OR a completed recording
  const hasText = value.trim().length > 0;
  const hasRecording = !!recorder.recording;
  const canSubmit = !isProcessing && (hasText || hasRecording);

  // Append transcript to textarea when speech recognition produces results
  useEffect(() => {
    if (speech.transcript) {
      const prev = valueRef.current;
      const separator = prev && !prev.endsWith(' ') ? ' ' : '';
      const appended = prev + separator + speech.transcript;
      setValue(appended);
      piiScan(appended);
      setDismissedPii(false);
      setWasTranscribed(true);
    }
  }, [speech.transcript, piiScan]);

  // NOTE: No PII gate on submit — intentionally advisory-only.
  // Server-side Presidio is the security boundary; client WASM is bypassable UX feedback.
  const handleSubmit = useCallback(() => {
    if (isProcessing) return;

    // Priority: recording > text
    if (hasRecording && onSubmitAudio) {
      onSubmitAudio(recorder.recording!.blob, {
        duration: recorder.recording!.duration,
      });
      recorder.clearRecording();
      setValue('');
      setWasTranscribed(false);
      return;
    }

    if (hasText) {
      onSubmit(value.trim(), detected.type);
      setValue('');
      setWasTranscribed(false);
    }
  }, [
    value, detected.type, onSubmit, onSubmitAudio, isProcessing,
    hasText, hasRecording, recorder,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setWasTranscribed(false);
    setDismissedPii(false);
    piiScan(newValue);
  }, [piiScan]);

  // PII action handlers
  const handlePiiMask = useCallback(() => {
    if (!piiResult?.findings) return;
    const sorted = [...piiResult.findings].sort((a, b) => b.start - a.start);
    let masked = value;
    for (const f of sorted) {
      masked = masked.slice(0, f.start) + `[${f.entity_type}_MASKED]` + masked.slice(f.end);
    }
    setValue(masked);
    piiClear();
  }, [value, piiResult, piiClear]);

  const handlePiiRemove = useCallback(() => {
    if (!piiResult?.findings) return;
    const sorted = [...piiResult.findings].sort((a, b) => b.start - a.start);
    let cleaned = value;
    for (const f of sorted) {
      cleaned = cleaned.slice(0, f.start) + cleaned.slice(f.end);
    }
    setValue(cleaned.replace(/\s{2,}/g, ' ').trim());
    piiClear();
  }, [value, piiResult, piiClear]);

  const handlePiiSendAnyway = useCallback(() => {
    setDismissedPii(true);
  }, []);

  const handleSelectAndStart = useCallback(
    (newMode: MicMode) => {
      // Stop any active mic first
      if (speech.isListening) speech.stopListening();
      if (recorder.isRecording) recorder.stopRecording();

      setMicMode(newMode);
      localStorage.setItem('crucio-mic-mode', newMode);

      if (newMode === 'transcribe') {
        speech.startListening();
      } else {
        recorder.startRecording();
      }
    },
    [speech, recorder],
  );

  const handleStopMic = useCallback(() => {
    if (speech.isListening) speech.stopListening();
    if (recorder.isRecording) recorder.stopRecording();
  }, [speech, recorder]);

  return (
    <div className="widget overflow-visible">
      <div className="p-3">
        {/* Type badge + URL indicator */}
        <div className="h-5 mb-1.5 flex items-center gap-2">
          {(hasText || hasRecording) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium bg-bg-hover text-text-muted">
              <BadgeIcon className="w-2.5 h-2.5" />
              {hasRecording ? 'Audio recording' : detected.label}
            </span>
          )}
          {detected.extractedUrls.length > 0 && detected.type === 'TEXT' && (
            <span className="inline-flex items-center gap-1 text-[10px] text-accent-blue/70">
              <Link2 className="w-2.5 h-2.5" />
              {detected.extractedUrls.length} link{detected.extractedUrls.length > 1 ? 's' : ''} will be extracted
            </span>
          )}
        </div>

        {/* Audio attachment chip */}
        {recorder.recording && (
          <div className="mb-2">
            <AudioAttachmentChip
              recording={recorder.recording}
              onRemove={recorder.clearRecording}
            />
          </div>
        )}

        {/* Input row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 flex">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Enter a note, paste a URL, or use the mic..."
              rows={2}
              className="w-full bg-bg-primary border border-border rounded-lg px-3 py-2 pr-10 text-xs text-text-primary placeholder:text-text-muted/50 focus:outline-hidden focus:border-accent-orange/50 resize-none font-mono"
            />

            {/* Mic button — vertically centered in textarea */}
            <div className="absolute top-1/2 -translate-y-1/2 right-1.5">
              <MicButton
                mode={micMode}
                isActive={isMicActive}
                isRecording={recorder.isRecording}
                transcribeSupported={speech.isSupported}
                recordSupported={recorder.isSupported}
                onSelectAndStart={handleSelectAndStart}
                onStop={handleStopMic}
              />
            </div>

            {/* Recording duration overlay */}
            {recorder.isRecording && (
              <div className="absolute top-1 left-3 flex items-center gap-1.5 text-[10px] text-red-400">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Recording {recorder.duration}s
              </div>
            )}
          </div>

          {/* Send button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="self-center px-3 py-2 bg-accent-orange/15 text-accent-orange border border-accent-orange/30 rounded-lg hover:bg-accent-orange/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Error message from mic */}
        {micError && (
          <p className="mt-1.5 text-[10px] text-red-400">{micError}</p>
        )}

        {/* PII warning banner */}
        {hasPii && !dismissedPii && piiResult && (
          <PiiWarningBanner
            findings={piiResult.findings}
            inputText={value}
            onMask={handlePiiMask}
            onRemove={handlePiiRemove}
            onSendAnyway={handlePiiSendAnyway}
          />
        )}

        {/* Help text */}
        <div className="mt-1.5 flex items-center justify-between text-[10px] sm:text-xs text-text-muted/60">
          <span className="truncate">Try: PII content, injection attempts, or paste a URL</span>
          <span className="font-mono hidden sm:inline shrink-0 ml-2">⌘+Enter</span>
        </div>
      </div>
    </div>
  );
}
